import { Component, OnInit, ViewChild } from '@angular/core';
import { environment } from 'environments/environment';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Params } from '@angular/router';
import { NotificationService } from 'app/_services/notification.service';
import { SiteIncidentsService } from 'app/_services/site-incidents.service';
import { SiteService } from 'app/_services/site.service';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-site-incidents',
  templateUrl: './site-incidents.component.html',
})
export class SiteIncidentsComponent implements OnInit {
  private selectedSiteId: any;
  public selectedSite; any;
  public selectedIncident: any;

  public showReportIncidentModal: boolean = false;
  public showUpdateIncidentModal: boolean = false;
  public showUserModal: boolean = false;

  public reportIncidentForm: FormGroup;
  public updateIncidentForm: FormGroup;

  public selectedStatusFilter;
  public fromDateFilter: Date;
  public toDateFilter: Date;
  public isFilterLoading: boolean = false;
  public datepickerYearRange = '2023 : '+ new Date().getFullYear();

  public isSaving = false; // helps prevent double click submit

  public incidentType: { label: string; value: string  }[] = [
    { label: 'Emergency', value: 'EMERGENCY' },
    { label: 'Health & Safety', value: 'HEALTH_AND_SAFETY' },
    { label: 'Security', value: 'SECURITY' },
    { label: 'Environmental', value: 'ENVIRONMENTAL' },
    { label: 'Equiment Failure', value: 'EQUIPMENT_FAILURE' },
    { label: 'Operational', value: 'OPERATIONAL' },
    { label: 'Medical', value: 'MEDICAL' },
  ];

  public severity: { label: string; value: string  }[] = [
    { label: 'Serious Injury', value: 'SERIOUS_INJURY' },
    { label: 'Walking Wounded', value: 'WALKING_WOUNDED' },
    { label: 'Minor Injury', value: 'MINOR_INJURY' },
    { label: 'Near Miss', value: 'NEAR_MISS' },
    { label: 'Equipment Damage', value: 'EQUIPMENT_DAMAGE' },
    { label: 'Economic Loss', value: 'ECONOMIC_LOSS' },
  ];

  public status: { label: string; value: string  }[] = [
    { label: 'Reported', value: 'REPORTED' },
    { label: 'Under Review', value: 'REVIEW' },
    { label: 'Resolved', value: 'RESOLVED' },
    { label: 'Closed', value: 'CLOSED' },
  ];

  public statusFilter: { label: string; value: string  }[];


  // table data
  public incidents: any[] = [];
  public pageSizes: any = environment.pageSizes;
  public pagination: any;
  @ViewChild('paginator', { static: true }) paginator;

  constructor(
    private siteService: SiteService,
    private siteIncidentsService: SiteIncidentsService,
    public notificationService: NotificationService,
    private activatedRoute: ActivatedRoute,
    fb: FormBuilder,
  ) {
    this.statusFilter = [{ label: 'All', value: undefined }, ...this.status];
    activatedRoute.params.subscribe((params: Params) => {
      this.selectedSiteId = params.id;
    });

    this.reportIncidentForm = fb.group({
      description: new FormControl('', Validators.required),
      resolution: new FormControl(),
      type: new FormControl('', Validators.required),
      severity: new FormControl('', Validators.required),
    });

    this.updateIncidentForm = fb.group({
      description: new FormControl('', Validators.required),
      resolution: new FormControl(),
      type: new FormControl('', Validators.required),
      severity: new FormControl('', Validators.required),
      status: new FormControl('', Validators.required),
      incidentId: new FormControl('', Validators.required),
    });

  }

  ngOnInit() {
    this.activatedRoute.params.subscribe((params: Params) => {
      this.getSelectedSite(this.selectedSiteId);
      this.resetPagination();
    });

  }

  public getSelectedSite(id) {
    this.siteService.getSiteById(id).subscribe((data: any) => {
      this.selectedSite = data;
    });
  }

  public getIncidentsForSite(siteId, pagination) {
    const selectedStatus = this.selectedStatusFilter ? this.selectedStatusFilter.value : undefined;

    const selectedFromDate = this.fromDateFilter ? this.fromDateFilter.toISOString() : undefined;
    let selectedToDate;
    if (this.toDateFilter) {
      this.toDateFilter.setHours(23, 59, 59, 999);
      selectedToDate = this.toDateFilter.toISOString();
    }

    this.siteIncidentsService.getIncidentsBySite(pagination, 'createdDate','desc', siteId, selectedFromDate, selectedToDate, selectedStatus).subscribe((data: any) => {
      this.incidents = data.body;
      this.incidents.forEach(incident => {
        if(incident.status==='REPORTED' && incident.type==='EMERGENCY'){
          incident.isReportedEmergency = true;
        }
      });
      this.isFilterLoading = false;
      this.pagination.totalRecords = Number(data.headers.get('X-Total-Count'));
    });
  }

  // Add a new restriction
  public reportIncidentModal() {
    this.isSaving = false;
    this.reportIncidentForm.reset();
    this.showReportIncidentModal = true;
  }

  public updateIncident(incident: any) {
    this.isSaving = false;
    const incidentStatus = this.status.find(item => item.value === incident.status);
    const incidentSeverity = this.severity.find(item => item.value === incident.severity);
    const incidentType = this.incidentType.find(item => item.value === incident.type);


    this.updateIncidentForm.reset();
    this.updateIncidentForm.patchValue({
        description: incident.description,
        resolution: incident.resolution,
        status: incidentStatus,
        severity: incidentSeverity,
        type: incidentType,
        incidentId: incident.id,
      }
    );
    this.showUpdateIncidentModal = true;
  }

  public async onReportIncidentFormSubmit() {
    this.isSaving = true;

    const incident = {
      description: this.reportIncidentForm.value.description,
      resolution: this.reportIncidentForm.value.resolution,
      type: this.reportIncidentForm.value.type.value,
      severity: this.reportIncidentForm.value.severity.value,
      siteId: this.selectedSiteId,
    };

    this.siteIncidentsService.addIncident(incident).subscribe(() => {
      this.isSaving = false;
      this.showReportIncidentModal = false;
      this.resetPagination();
      this.notificationService.notify('success', 'Success', 'New Site Incident Reported');
    });
  }

  public async onUpdateIncidentFormSubmit() {
    this.isSaving = true;

    const incident = {
      description: this.updateIncidentForm.value.description,
      resolution: this.updateIncidentForm.value.resolution,
      type: this.updateIncidentForm.value.type.value,
      severity: this.updateIncidentForm.value.severity.value,
      status:  this.updateIncidentForm.value.status.value,
    };
    const incidentId = this.updateIncidentForm.value.incidentId;

    this.siteIncidentsService.updateSiteIncident(incidentId, incident).subscribe(() => {
      this.isSaving = false;
      this.showUpdateIncidentModal = false;
      this.resetPagination();
      this.notificationService.notify('success', 'Success', 'Site Incident Updated');
    });
  }

  public paginate(event) {
    this.pagination.size = event.rows;
    this.pagination.page = event.page;
    this.getIncidentsForSite(this.selectedSiteId, this.pagination);
  }

  private resetPagination() {
    this.pagination = {
      size: this.pageSizes[0],
      page: 0,
      totalRecords: 0,
    };
    this.goToFirstPage();
    this.getIncidentsForSite(this.selectedSiteId, this.pagination);
  }

  private goToFirstPage() {
    this.pagination.page = 0;      // Backend: reset page index to fetch from API
    if (this.paginator) {
      this.paginator.first = 0;    // Frontend: update visual page indicator (PrimeNg)
    }
  }

  public onStatusChanged(event){
    this.selectedStatusFilter = event.value.value;
    this.goToFirstPage();
    this.getIncidentsForSite(this.selectedSiteId, this.pagination);
  }

  public onFilterChanged(event){
    this.isFilterLoading = true;
    this.goToFirstPage();
    this.getIncidentsForSite(this.selectedSiteId, this.pagination);
  }

  public viewUser(incident){
      this.selectedIncident = incident;
      this.showUserModal = true;
  }

}
